// src/provider.tsx
import { useEffect, useMemo, useState } from "react";

// src/context.ts
import { createContext } from "react";
var FiasBridgeContext = createContext(null);

// src/bridge.ts
var messageCounter = 0;
function generateMessageId() {
  return `msg_${Date.now()}_${++messageCounter}`;
}
var DEFAULT_TIMEOUT_MS = 3e4;
var FiasBridge = class {
  constructor() {
    this.pendingRequests = /* @__PURE__ */ new Map();
    this.initialized = false;
    this.initPromise = null;
    this.initResolve = null;
    this.archId = "";
    this.permissions = [];
    this.theme = null;
    this.currentPath = "";
    this.hostOrigin = "";
    this.themeListeners = /* @__PURE__ */ new Set();
    this.navigationListeners = /* @__PURE__ */ new Set();
    this.stepNavigationListeners = /* @__PURE__ */ new Set();
    this.listening = false;
    this.handleMessage = (event) => {
      const data = event.data;
      if (!data || typeof data !== "object" || !data.type) return;
      if (data.type === "init") {
        if (this.initialized) return;
        this.hostOrigin = event.origin;
        const initMsg = data;
        this.archId = initMsg.payload.archId;
        this.permissions = initMsg.payload.permissions;
        this.theme = initMsg.payload.theme;
        this.currentPath = initMsg.payload.currentPath;
        this.initialized = true;
        this.initResolve?.();
        return;
      }
      if (this.hostOrigin && event.origin !== this.hostOrigin) return;
      if (data.type === "theme_update") {
        this.theme = data.payload;
        for (const listener of this.themeListeners) {
          listener(this.theme);
        }
        return;
      }
      if (data.type === "navigate_update") {
        this.currentPath = data.payload;
        for (const listener of this.navigationListeners) {
          listener(this.currentPath);
        }
        return;
      }
      if (data.type === "step_navigate") {
        const { nodeId } = data.payload;
        if (typeof nodeId === "string") {
          for (const listener of this.stepNavigationListeners) {
            listener(nodeId);
          }
        }
        return;
      }
      if (data.type === "stream_token") {
        const { messageId, text } = data;
        const pending = this.pendingRequests.get(messageId);
        if (pending?.onToken && typeof text === "string") {
          pending.onToken(text);
        }
        return;
      }
      if (data.type === "response") {
        const response = data;
        const pending = this.pendingRequests.get(response.messageId);
        if (!pending) return;
        clearTimeout(pending.timer);
        this.pendingRequests.delete(response.messageId);
        if (response.error) {
          pending.reject(new Error(response.error));
        } else {
          pending.resolve(response.payload);
        }
      }
    };
    this.initPromise = new Promise((resolve) => {
      this.initResolve = resolve;
    });
    this.attach();
  }
  /**
   * Register the message listener. Safe to call multiple times —
   * will not add duplicate listeners.
   */
  attach() {
    if (typeof window !== "undefined" && !this.listening) {
      window.addEventListener("message", this.handleMessage);
      this.listening = true;
    }
  }
  /**
   * Wait for the host to send the init message.
   */
  async waitForInit() {
    if (this.initialized) return;
    return this.initPromise;
  }
  /**
   * Signal to the host that the plugin is loaded and ready.
   */
  ready() {
    this.send("ready", {});
  }
  /**
   * Request the host to resize the plugin iframe.
   */
  resize(height) {
    this.send("resize", { height });
  }
  /**
   * Show a platform toast notification.
   */
  showToast(message, variant = "info") {
    this.send("toast", { message, variant });
  }
  /**
   * Send a request to the host and wait for a response.
   */
  async request(type, payload) {
    await this.waitForInit();
    return new Promise((resolve, reject) => {
      const messageId = generateMessageId();
      const timer = setTimeout(() => {
        this.pendingRequests.delete(messageId);
        reject(new Error(`Bridge request timed out: ${type}`));
      }, DEFAULT_TIMEOUT_MS);
      this.pendingRequests.set(messageId, {
        resolve,
        reject,
        timer
      });
      this.send(type, payload, messageId);
    });
  }
  getArchId() {
    return this.archId;
  }
  getPermissions() {
    return [...this.permissions];
  }
  getTheme() {
    return this.theme;
  }
  getCurrentPath() {
    return this.currentPath;
  }
  onThemeUpdate(listener) {
    this.themeListeners.add(listener);
    return () => this.themeListeners.delete(listener);
  }
  onNavigationUpdate(listener) {
    this.navigationListeners.add(listener);
    return () => this.navigationListeners.delete(listener);
  }
  onStepNavigationUpdate(listener) {
    this.stepNavigationListeners.add(listener);
    return () => this.stepNavigationListeners.delete(listener);
  }
  /**
   * Send a streaming entity_invoke request. Tokens arrive via stream_token
   * messages and are forwarded to the onToken callback. The returned promise
   * resolves with the final EntityInvocationResult when the host sends the
   * normal response message.
   */
  async requestStream(payload, onToken) {
    await this.waitForInit();
    return new Promise((resolve, reject) => {
      const messageId = generateMessageId();
      const timer = setTimeout(() => {
        this.pendingRequests.delete(messageId);
        reject(new Error("Bridge streaming request timed out: entity_invoke"));
      }, 12e4);
      this.pendingRequests.set(messageId, {
        resolve,
        reject,
        timer,
        onToken
      });
      this.send("entity_invoke", { ...payload, stream: true }, messageId);
    });
  }
  /**
   * Clean up event listeners and pending requests.
   * After destroy(), the bridge can be re-activated by calling attach().
   */
  destroy() {
    if (typeof window !== "undefined" && this.listening) {
      window.removeEventListener("message", this.handleMessage);
      this.listening = false;
    }
    for (const pending of this.pendingRequests.values()) {
      clearTimeout(pending.timer);
      pending.reject(new Error("Bridge destroyed"));
    }
    this.pendingRequests.clear();
    this.themeListeners.clear();
    this.navigationListeners.clear();
    this.stepNavigationListeners.clear();
  }
  send(type, payload, messageId) {
    if (typeof window === "undefined") return;
    const message = {
      type,
      messageId: messageId ?? generateMessageId(),
      payload
    };
    const targetOrigin = this.hostOrigin || "*";
    window.parent.postMessage(message, targetOrigin);
  }
};
var bridgeInstance = typeof window !== "undefined" ? new FiasBridge() : null;
function getBridge() {
  if (!bridgeInstance) {
    bridgeInstance = new FiasBridge();
  }
  return bridgeInstance;
}
function resetBridge() {
  bridgeInstance?.destroy();
  bridgeInstance = null;
}

// src/provider.tsx
import { jsx } from "react/jsx-runtime";
function FiasProvider({ children }) {
  const [ready, setReady] = useState(false);
  const bridge = useMemo(() => getBridge(), []);
  useEffect(() => {
    let mounted = true;
    bridge.waitForInit().then(() => {
      if (mounted) {
        bridge.ready();
        setReady(true);
      }
    });
    return () => {
      mounted = false;
    };
  }, [bridge]);
  if (!ready) {
    return null;
  }
  return /* @__PURE__ */ jsx(FiasBridgeContext.Provider, { value: bridge, children });
}

// src/hooks.ts
import { useCallback, useContext, useEffect as useEffect2, useRef, useState as useState2 } from "react";
function useBridge() {
  const bridge = useContext(FiasBridgeContext);
  if (!bridge) {
    throw new Error("useBridge must be used within a <FiasProvider>");
  }
  return bridge;
}
function useFiasUser() {
  const bridge = useBridge();
  const [user, setUser] = useState2(null);
  useEffect2(() => {
    let mounted = true;
    bridge.request("get_user", {}).then((data) => {
      if (mounted) setUser(data);
    });
    return () => {
      mounted = false;
    };
  }, [bridge]);
  return user;
}
function useFiasTheme() {
  const bridge = useBridge();
  const [theme, setTheme] = useState2(bridge.getTheme());
  useEffect2(() => {
    return bridge.onThemeUpdate(setTheme);
  }, [bridge]);
  return theme;
}
function useFiasStorage() {
  const bridge = useBridge();
  const readFile = useCallback(
    (path) => bridge.request("storage_read", { path }),
    [bridge]
  );
  const writeFile = useCallback(
    (path, content) => bridge.request("storage_write", { path, content }),
    [bridge]
  );
  const listFiles = useCallback(
    (prefix) => bridge.request("storage_list", { prefix }),
    [bridge]
  );
  const deleteFile = useCallback(
    (path) => bridge.request("storage_delete", { path }),
    [bridge]
  );
  return { readFile, writeFile, listFiles, deleteFile };
}
function useEntityInvocation() {
  const bridge = useBridge();
  const [isLoading, setIsLoading] = useState2(false);
  const [result, setResult] = useState2(null);
  const [error, setError] = useState2(null);
  const [streamingText, setStreamingText] = useState2("");
  const invoke = useCallback(
    async (params) => {
      setIsLoading(true);
      setError(null);
      setStreamingText("");
      try {
        const res = await bridge.requestStream(params, (token) => {
          setStreamingText((prev) => prev + token);
        });
        setResult(res);
        return res;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [bridge]
  );
  return { invoke, isLoading, result, error, streamingText };
}
function useFiasNavigation() {
  const bridge = useBridge();
  const [currentPath, setCurrentPath] = useState2(bridge.getCurrentPath());
  useEffect2(() => {
    return bridge.onNavigationUpdate(setCurrentPath);
  }, [bridge]);
  const navigateTo = useCallback(
    (path) => {
      bridge.request("navigate", { path });
    },
    [bridge]
  );
  return { navigateTo, currentPath };
}
function usePersistentState(key, initialValue) {
  const bridge = useBridge();
  const [value, setValueInternal] = useState2(initialValue);
  const initializedRef = useRef(false);
  useEffect2(() => {
    bridge.request("storage_read", { path: `__state/${key}` }).then((result) => {
      if (result.exists && result.content !== null) {
        try {
          setValueInternal(JSON.parse(result.content));
        } catch {
        }
      }
      initializedRef.current = true;
    });
  }, [bridge, key]);
  const setValue = useCallback(
    (next) => {
      setValueInternal((prev) => {
        const resolved = typeof next === "function" ? next(prev) : next;
        bridge.request("storage_write", {
          path: `__state/${key}`,
          content: JSON.stringify(resolved)
        }).catch(() => {
        });
        return resolved;
      });
    },
    [bridge, key]
  );
  return [value, setValue];
}
function useStepNavigation(initialStep) {
  const bridge = useBridge();
  const [currentStep, setCurrentStep] = useState2(initialStep ?? null);
  useEffect2(() => {
    return bridge.onStepNavigationUpdate((nodeId) => {
      setCurrentStep(nodeId);
    });
  }, [bridge]);
  return { currentStep, setCurrentStep };
}

// src/fias.ts
var fias = {
  /**
   * Signal to the host that the plugin is loaded and ready.
   * Called automatically by FiasProvider — only use manually
   * if not using React.
   */
  ready() {
    getBridge().ready();
  },
  /**
   * Request the host to resize the plugin iframe to the given height.
   */
  resize(height) {
    getBridge().resize(height);
  },
  /**
   * Show a platform toast notification.
   */
  showToast(message, variant = "info") {
    getBridge().showToast(message, variant);
  }
};

// src/theme.ts
var SYSTEM_FONTS = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
var MONO_FONTS = '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace';
var SHARED_SPACING = { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px" };
var SHARED_FONTS = { body: SYSTEM_FONTS, heading: SYSTEM_FONTS, mono: MONO_FONTS };
var DARK_THEME = {
  mode: "dark",
  colors: {
    primary: "#ffffff",
    secondary: "#1f1f1f",
    background: "#0a0a0a",
    surface: "#171717",
    text: "#ffffff",
    textSecondary: "#a6a6a6",
    border: "#2e2e2e",
    error: "#ef4444",
    warning: "#f59e0b",
    success: "#22c55e"
  },
  spacing: SHARED_SPACING,
  fonts: SHARED_FONTS
};
var LIGHT_THEME = {
  mode: "light",
  colors: {
    primary: "#171717",
    secondary: "#e5e5e5",
    background: "#ffffff",
    surface: "#fafafa",
    text: "#0a0a0a",
    textSecondary: "#737373",
    border: "#e5e5e5",
    error: "#dc2626",
    warning: "#d97706",
    success: "#16a34a"
  },
  spacing: SHARED_SPACING,
  fonts: SHARED_FONTS
};
function getDefaultTheme(mode) {
  return mode === "light" ? LIGHT_THEME : DARK_THEME;
}
export {
  DARK_THEME,
  FiasBridge,
  FiasProvider,
  LIGHT_THEME,
  fias,
  getBridge,
  getDefaultTheme,
  resetBridge,
  useEntityInvocation,
  useFiasNavigation,
  useFiasStorage,
  useFiasTheme,
  useFiasUser,
  usePersistentState,
  useStepNavigation
};
